import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { 
  Users, UserPlus, Loader2, Crown, Shield, Eye, Trash2, 
  Mail, CheckCircle2, Clock, Building2
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const ROLE_ICONS = {
  owner: Crown,
  admin: Shield,
  member: Users,
  viewer: Eye
};

const ROLE_COLORS = {
  owner: 'text-amber-400',
  admin: 'text-blue-400',
  member: 'text-emerald-400',
  viewer: 'text-slate-400'
};

const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer'
};

export default function TeamsPage() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create team state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Invite member state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);

  const loadTeamData = useCallback(async () => {
    try {
      setLoading(true);
      const teamRes = await api.get('/teams/my-team');
      
      if (teamRes.data.team) {
        setTeam(teamRes.data.team);
        setStats(teamRes.data.stats);
        
        // Load members
        const membersRes = await api.get('/teams/members');
        setMembers(membersRes.data.members || []);
        setPendingInvites(membersRes.data.pending_invites || []);
      }
    } catch (error) {
      console.error('Failed to load team:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeamData();
  }, [loadTeamData]);

  const handleCreateTeam = async () => {
    if (!teamName) {
      toast.error('Please enter a team name');
      return;
    }

    setCreating(true);
    try {
      await api.post('/teams/create', {
        team_name: teamName,
        company_name: companyName || undefined
      });
      
      toast.success('Team created successfully!');
      setShowCreateDialog(false);
      setTeamName('');
      setCompanyName('');
      loadTeamData();
    } catch (error) {
      console.error('Failed to create team:', error);
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setInviting(true);
    try {
      await api.post('/teams/invite', {
        email: inviteEmail,
        role: inviteRole
      });
      
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteDialog(false);
      setInviteEmail('');
      setInviteRole('member');
      loadTeamData();
    } catch (error) {
      console.error('Failed to invite member:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/teams/members/${userId}/role`, { role: newRole });
      toast.success('Member role updated');
      loadTeamData();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      await api.delete(`/teams/members/${userId}`);
      toast.success('Member removed');
      loadTeamData();
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!team) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <div className="stat-card text-center py-12">
            <Users className="w-16 h-16 text-blue-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Create Your Team</h2>
            <p className="text-slate-400 mb-6">
              Collaborate with your team on corporate structures
            </p>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="gradient-accent">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Team
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-800">
                <DialogHeader>
                  <DialogTitle>Create a New Team</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Set up your team to collaborate on corporate structures
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Team Name *</Label>
                    <Input
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder="Acme Legal Team"
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Company Name (Optional)</Label>
                    <Input
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Acme Corporation"
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                    className="border-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateTeam}
                    disabled={creating}
                    className="gradient-accent"
                  >
                    {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create Team
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const currentUserMember = members.find(m => m.user_id === user?.id);
  const canManageTeam = currentUserMember?.permissions?.can_manage_team || false;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-400" />
              {team.name}
            </h1>
            {team.company_name && (
              <p className="text-slate-400 mt-1 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {team.company_name}
              </p>
            )}
          </div>
          
          {canManageTeam && (
            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
              <DialogTrigger asChild>
                <Button className="gradient-accent">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Invite Member
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-800">
                <DialogHeader>
                  <DialogTitle>Invite Team Member</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Send an invitation to join your team
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Email Address *</Label>
                    <Input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="colleague@company.com"
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="admin">Admin - Full access except billing</SelectItem>
                        <SelectItem value="member">Member - Can create & edit</SelectItem>
                        <SelectItem value="viewer">Viewer - Read-only access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowInviteDialog(false)}
                    className="border-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleInviteMember}
                    disabled={inviting}
                    className="gradient-accent"
                  >
                    {inviting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Send Invitation
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="stat-card">
              <p className="text-sm text-slate-400 mb-1">Team Members</p>
              <p className="text-3xl font-bold text-blue-400">{stats.members}</p>
            </div>
            <div className="stat-card">
              <p className="text-sm text-slate-400 mb-1">Entities</p>
              <p className="text-3xl font-bold text-emerald-400">{stats.entities}</p>
            </div>
            <div className="stat-card">
              <p className="text-sm text-slate-400 mb-1">Audits</p>
              <p className="text-3xl font-bold text-amber-400">{stats.audits}</p>
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="stat-card">
          <h2 className="text-xl font-semibold mb-4">Team Members</h2>
          
          <div className="space-y-3">
            {members.map((member) => {
              const RoleIcon = ROLE_ICONS[member.role] || Users;
              const isCurrentUser = member.user_id === user?.id;
              
              return (
                <div
                  key={member.id}
                  className="bg-slate-800/50 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold`}>
                      {member.user?.full_name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {member.user?.full_name || 'Unknown'}
                          {isCurrentUser && <span className="text-xs text-slate-500">(You)</span>}
                        </p>
                      </div>
                      <p className="text-sm text-slate-400">{member.user?.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {canManageTeam && member.role !== 'owner' && !isCurrentUser ? (
                      <Select
                        value={member.role}
                        onValueChange={(newRole) => handleUpdateRole(member.user_id, newRole)}
                      >
                        <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-sm">
                          <RoleIcon className={`w-4 h-4 mr-2 ${ROLE_COLORS[member.role]}`} />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-lg bg-slate-800 ${ROLE_COLORS[member.role]}`}>
                        <RoleIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">{ROLE_LABELS[member.role]}</span>
                      </div>
                    )}
                    
                    {canManageTeam && member.role !== 'owner' && !isCurrentUser && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <div className="stat-card">
            <h2 className="text-xl font-semibold mb-4">Pending Invitations</h2>
            
            <div className="space-y-3">
              {pendingInvites.map((invite) => {
                const RoleIcon = ROLE_ICONS[invite.role] || Users;
                
                return (
                  <div
                    key={invite.id}
                    className="bg-slate-800/50 rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="font-medium">{invite.email}</p>
                        <p className="text-sm text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Invited {new Date(invite.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-lg bg-slate-800 ${ROLE_COLORS[invite.role]}`}>
                      <RoleIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">{ROLE_LABELS[invite.role]}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
